// pages/CreateUser.tsx — name + password only, auto login_id
import { useState, useEffect } from 'react';
import { Eye, EyeOff, Copy, CheckCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import useAdmin from '../hooks/useAdmin';
import { toast } from 'react-hot-toast';
import { copyText } from '../../shared/clipboard';

interface FormData { name: string; login_id?: string; password: string; managerId: string; initialCredits: number; }
interface Manager { id: string; name: string; login_id?: string; email?: string; }

export default function CreateUser() {
  const [managers, setManagers] = useState<Manager[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createdLoginId, setCreatedLoginId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { token, refreshCsrf, error, setError, fetchManagers } = useAdmin();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    defaultValues: { managerId: '', initialCredits: 0 },
  });

  useEffect(() => {
    if (!token) return;
    fetchManagers().then(data => setManagers((data || []) as any[])).catch(() => {});
    refreshCsrf();
  }, [token, fetchManagers, refreshCsrf]);

  const copyToClipboard = async (text: string) => {
    try {
      await copyText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed. Select and copy manually.');
    }
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true); setError(''); setCreatedLoginId(null);
    try {
      const csrfToken = await refreshCsrf();
      const res = await fetch('/admin/create-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || localStorage.getItem('adminToken')}`, 'X-CSRF-Token': csrfToken || '' },
        credentials: 'include',
        body: JSON.stringify({
          name: data.name.trim(), password: data.password, mode: 'cash',
          login_id: data.login_id?.trim() || undefined,
          manager_id: data.managerId || null, initialCredits: Number(data.initialCredits || 0),
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error?.message || 'Failed to create user');
      const loginId = result.login_id || result.user?.login_id;
      setCreatedLoginId(loginId);
      toast.success(`User created! Login ID: ${loginId}`);
      reset();
    } catch (err: any) {
      setError(err.message); toast.error(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Create New User</h2>

      {createdLoginId && (
        <div className="mb-6 p-5 bg-green-50 border-2 border-green-400 rounded-xl">
          <p className="text-green-800 font-bold text-lg mb-2">✅ User Created Successfully!</p>
          <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-green-300">
            <div>
              <p className="text-xs text-gray-500 mb-1">User Login ID</p>
              <p className="text-2xl font-mono font-bold text-indigo-700 tracking-widest">{createdLoginId}</p>
            </div>
            <button onClick={() => copyToClipboard(createdLoginId)}
              className="ml-auto p-2 bg-indigo-100 hover:bg-indigo-200 rounded-lg transition">
              {copied ? <CheckCircle size={20} className="text-green-600" /> : <Copy size={20} className="text-indigo-600" />}
            </button>
          </div>
          <p className="mt-2 text-sm text-green-700">Share this Login ID and password with the user. They will use it to play.</p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between">
          <span>{error}</span><button onClick={() => setError('')} className="font-bold">✕</button>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Player Name"
              {...register('name', { required: 'Name is required' })} />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User ID (Optional)</label>
            <input className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Leave blank to auto-generate"
              {...register('login_id', { pattern: { value: /^[A-Za-z0-9_-]{3,20}$/, message: '3-20 letters, numbers, _ or -' } })} />
            {errors.login_id && <p className="mt-1 text-sm text-red-600">{errors.login_id.message}</p>}
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input type={showPassword ? 'text' : 'password'}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 pr-12"
              placeholder="At least 8 characters"
              {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Min 8 characters' } })} />
            <button type="button" className="absolute right-3 top-10 text-gray-600" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assign Manager (Optional)</label>
            <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" {...register('managerId')}>
              <option value="">No Manager</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.login_id})</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Initial Credits</label>
            <input
              type="number"
              min="0"
              step="1"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="0"
              {...register('initialCredits', {
                valueAsNumber: true,
                min: { value: 0, message: 'Initial credits cannot be negative' },
              })}
            />
            {errors.initialCredits && <p className="mt-1 text-sm text-red-600">{errors.initialCredits.message}</p>}
          </div>

          <button type="submit" disabled={loading}
            className={`w-full py-3 px-4 font-medium rounded-lg text-white transition ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
            {loading ? 'Creating...' : 'Create User'}
          </button>
        </form>
      </div>
      <p className="mt-4 text-xs text-gray-400 text-center">Enter a User ID to use it as Login ID, or leave blank to auto-generate USR######.</p>
    </div>
  );
}
